/**
 * PRESENTER STAGE — the widget's half of agent-led demos
 * (docs/PRESENTER_AGENT.md §5). Lives only on presenter mounts
 * (`config.projectPublicId` set — the /p/?present=1 shell).
 *
 * Routes the five stage ChatEvents into the surfaces the widget already
 * has: `tour-present` opens the tour in the content pane (agent mode),
 * `tour-control` drives it through the shipped `__spTourControl` facade,
 * `tour-focus` forwards to the player's focus API (highlight ring, no
 * card). `screen-show` and `checklist-control` are ACCEPTED but inert in
 * this slice (footage improv + the agenda pane land next) — consuming
 * them keeps the dispatcher forward-compatible with the server.
 *
 * Also owns the CONTINUATION TICK — text-mode pacing (agent-freewill
 * law): after a turn that moved the stage, wait a reading-dwell and send
 * one hidden nudge so the agent decides advance vs. hold. Ticks stop
 * after MAX_CONSECUTIVE_TICKS without real viewer input, so an
 * abandoned tab never grinds turns forever.
 */

import type { ChatEvent } from '@showpilot/core';
import type { SpTourControl } from './tours/control';

export type PresenterStageDeps = {
  /** Open a tour in the content pane, agent mode. */
  openTour: (tourPublicId: string) => void;
  /** Tear the tour viewer down — the visitor sees the page again. */
  closeStage?: () => void;
  /** The live control facade — undefined until a player has mounted. */
  getControl: () => SpTourControl | undefined;
  /** Send one hidden user turn (never rendered as a visitor bubble). */
  sendTick: (text: string) => void;
  /** One-voice law: narrated media on stage — the tick stands down. */
  isMediaHolding?: () => boolean;
  /** Stage-truth loop: an agent stage command is being applied — the
      next player step event within the window is the AGENT's move, not
      the viewer's. */
  onAgentMove?: () => void;
  /** Test seams; default to the real timers. */
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (t: ReturnType<typeof setTimeout>) => void;
};

/** Reading dwell before the continuation tick fires. */
export const TICK_DWELL_MS = 8000;
/** Ticks allowed without any real viewer input in between. */
export const MAX_CONSECUTIVE_TICKS = 2;
/** How long we retry a control op while the player is still mounting. */
const CONTROL_RETRY_MS = 250;
const CONTROL_RETRY_MAX = 16; // 4s

/** The hidden nudge. The server's PRESENTING stance carries the craft;
    this only marks the moment. */
export const TICK_TEXT =
  '(continuation tick — the viewer is watching quietly. If the moment has landed, advance the stage and say what the next one means; if you are waiting on them, send nothing.)';

export type PresenterStage = {
  /** Route one ChatEvent. Returns true when it was a stage event. */
  handleEvent(event: ChatEvent): boolean;
  /** A REAL viewer action (typed message, chip tap) — resets the tick budget. */
  noteViewerInput(): void;
  /** The turn's `done` event — maybe schedule the continuation tick. */
  onTurnDone(): void;
  /** The player reached the tour's end (or its completion step): the
      story is told, so no continuation tick until the viewer speaks or
      a new present (2026-09-22: a tick after the end re-closed the demo). */
  noteTourCompleted(): void;
  /**
   * Voice call up/down. While a call is live the TICK stands down —
   * voice paces itself (several beats per reply); a text tick mid-call
   * would run a second presenter against the bot.
   */
  setVoiceActive(active: boolean): void;
  dispose(): void;
};

export function createPresenterStage(deps: PresenterStageDeps): PresenterStage {
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((t) => clearTimeout(t));

  let stageMovedThisTurn = false;
  let tourEnded = false;
  let voiceActive = false;
  let consecutiveTicks = 0;
  let tickTimer: ReturnType<typeof setTimeout> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const cancelTick = () => {
    if (tickTimer != null) {
      clearTimer(tickTimer);
      tickTimer = null;
    }
  };

  /** Run a facade op now, or keep trying briefly — tour-present is async
      (lazy chunk + snapshot fetch), so control arrives a beat later. */
  const withControl = (op: (c: SpTourControl) => void, attempt = 0): void => {
    if (disposed) return;
    const c = deps.getControl();
    if (c) {
      op(c);
      return;
    }
    if (attempt >= CONTROL_RETRY_MAX) {
      console.info('[presenter] no player');
      return;
    }
    retryTimer = setTimer(() => withControl(op, attempt + 1), CONTROL_RETRY_MS);
  };

  return {
    handleEvent(event: ChatEvent): boolean {
      switch (event.type) {
        case 'tour-present': {
          const p = event.payload;
          stageMovedThisTurn = true;
          tourEnded = false;
          deps.onAgentMove?.();
          deps.openTour(p.tourPublicId);
          if (typeof p.screen === 'number' && p.screen >= 1) {
            const screen = p.screen;
            // Screen numbers are 1-indexed on the wire; the facade's
            // goTo takes a 0-indexed step. Screen ≈ step for generated
            // tours (one step per screen) — goTo clamps if not. Screen 1
            // is NOT skipped: a re-present of the tour already on the
            // stage is a restart, and the stage must actually go back to
            // the top (live 2026-09-08: narrated screen 1 over screen 9).
            withControl((c) => c.goTo(screen - 1));
          }
          return true;
        }
        case 'tour-control': {
          const p = event.payload;
          // close: the demo is OVER — tear the viewer down and stand the
          // continuation tick down with it (never nudge a closed stage).
          if (p.op === 'close') {
            cancelTick();
            stageMovedThisTurn = false;
            deps.closeStage?.();
            return true;
          }
          stageMovedThisTurn = true;
          deps.onAgentMove?.();
          withControl((c) => {
            if (p.op === 'next') c.next();
            else if (p.op === 'back') c.back();
            else if (p.op === 'goTo') c.goTo(Math.max(0, (p.arg ?? 1) - 1));
            else if (p.op === 'seekTo') c.seekTo(p.arg ?? 0);
            // pause/resume: the facade's playPause is the tape toggle.
            else c.playPause();
          });
          return true;
        }
        case 'tour-focus': {
          const p = event.payload;
          withControl((c) => {
            if (c.focus) c.focus({ anchor: p.anchor, region: p.region });
            
          });
          return true;
        }
        case 'screen-show': {
          // Slice 2 — footage improv needs a bare-screen host. Accepted
          // so the server contract holds; nothing renders yet.
          console.info('[presenter] screen-show (slice 2)');
          return true;
        }
        case 'checklist-control': {
          // Slice 2 — the agenda pane. Accepted, inert.
          console.info('[presenter] agenda op (slice 2)');
          return true;
        }
        default:
          return false;
      }
    },

    noteViewerInput(): void {
      consecutiveTicks = 0;
      cancelTick();
      stageMovedThisTurn = false;
      tourEnded = false;
    },

    noteTourCompleted(): void {
      tourEnded = true;
      stageMovedThisTurn = false;
      cancelTick();
    },

    onTurnDone(): void {
      const moved = stageMovedThisTurn;
      stageMovedThisTurn = false;
      if (voiceActive) return;
      // One-voice law: narrated media has the floor — no nudge while a
      // clip is talking (its `ended` is the tick that brings us back).
      if (deps.isMediaHolding?.()) return;
      if (tourEnded || !moved) return;
      if (consecutiveTicks >= MAX_CONSECUTIVE_TICKS) return;
      cancelTick();
      tickTimer = setTimer(() => {
        tickTimer = null;
        if (disposed) return;
        // Re-check at fire time — a video may have started during the dwell.
        if (voiceActive || deps.isMediaHolding?.()) return;
        consecutiveTicks += 1;
        deps.sendTick(TICK_TEXT);
      }, TICK_DWELL_MS);
    },

    setVoiceActive(active: boolean): void {
      voiceActive = active;
      if (active) cancelTick();
    },

    dispose(): void {
      disposed = true;
      cancelTick();
      if (retryTimer != null) clearTimer(retryTimer);
    },
  };
}
